import * as Yup from 'yup';
import { useMemo, useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
// @mui
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
// hooks
import { Autocomplete, Button, Chip, MenuItem, TextField } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { useResponsive } from 'src/hooks/use-responsive';
// routes
import { paths } from 'src/routes/paths';
// components
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, {
    RHFSelect,
    RHFTextField,
    RHFUpload,
} from 'src/components/hook-form';

import { RouterLink } from 'src/routes/components';
import { uploadResumeToS3 } from 'src/services/aws.service';
import { useBoolean } from 'src/hooks/use-boolean';
import { LoadingScreen } from 'src/components/loading-screen';
import { useAuthContext } from 'src/auth/hooks';
import { getOrganizationId } from 'src/services/user.service';
import { CreateCandidate } from 'src/services/candidate.service';
// ----------------------------------------------------------------------

export default function AddCandidateView({ }) {
    // router
    const navigate = useNavigate();

    const STATUS_OPTIONS = [
        { value: 'Sourced', label: 'Sourced' },
        { value: 'Applied', label: 'Applied' },
        { value: 'Shortlisted', label: 'Shortlisted' },
        { value: 'Qualified', label: 'Qualified' },
        { value: 'Assessment', label: 'Assessment' },
        { value: 'Interview', label: 'Interview' },
        { value: 'Offer', label: 'Offer' },
        { value: 'Hired', label: 'Hired' },
    ];

    const SOURCE_OPTIONS = [
        { value: 'LinkedIn', label: 'LinkedIn' },
        { value: 'Naukri', label: 'Naukri' },
        { value: 'Others', label: 'Others' },
    ]


    // hooks
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuthContext();
    const organization_id = getOrganizationId(user);
    const dispatch = useDispatch();

    // states
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sourceInputValue, setSourceInputValue] = useState("");
    const [tagInputValue, setTagInputValue] = useState("");


    // memo
    const defaultValues = useMemo(
        () => ({
            source: SOURCE_OPTIONS[SOURCE_OPTIONS.length-1].label,
            tag: [],
            coverUrl: null,
            // status: STATUS_OPTIONS[0].value
        }),
        [],
    );

    const CandidateSchema = Yup.object().shape({
        source: Yup.string().required("Source is required"),
        tag: Yup.array().min(1, 'Enter at least one tag'),
        coverUrl: Yup.mixed().nullable(),
        // status: Yup.string()
    });

    const CandidateMethods = useForm({
        resolver: yupResolver(CandidateSchema),
        defaultValues: defaultValues,
    });

    const [methods, setMethods] = useState(CandidateMethods);

    // variables
    const { reset, setValue, handleSubmit } = methods;

    const onSubmit = handleSubmit(async (data, event) => {
        console.log(data);
        try {
            setIsSubmitting(true);
            // upload resume 
            const resume_url = await uploadResumeToS3(data.coverUrl);
            console.log(resume_url);

            // call api
            const payload = {
                resume_url,
                candidate_source: data.source,
                tags:data.tags
            }
            const res = await CreateCandidate(payload,organization_id);
            console.log(res);

            if(res.success){
                navigate(paths.dashboard.candidates.root);
                enqueueSnackbar("Candidate added successfully...")
            }
            else {
                enqueueSnackbar({message:res.message, variant:"error"});
            }
            setIsSubmitting(false);
            //   navigate();
        } catch (error) {
            console.error(error);
        }
    });

    const handleSingleDrop = useCallback(
        acceptedFiles => {
            const file = acceptedFiles[0];

            const newFile = Object.assign(file, {
                preview: URL.createObjectURL(file),
            });

            if (file) {
                setValue('coverUrl', newFile, { shouldValidate: true });
            }
        },
        [setValue],
    );

    const handleRemoveFile = (inputFile) => {
        setValue('coverUrl', null);
    };

    const renderProperties = (
        <Grid sx={{ width: '80%' }} xs={12}>
            <Stack
                spacing={1.5}
                direction="row"
                alignItems="center"
                sx={{
                    mb: { xs: 3, md: 5 },
                }}
            >
                <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ mb: '1rem' }}>
                        <Typography variant="h5">Add Canidate</Typography>
                    </Box>
                    <Box>
                        <Button
                            component={RouterLink}
                            href={`${paths.dashboard.candidates.root}`}
                            startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={16} />}
                        >
                            Back
                        </Button>
                    </Box>
                </Box>
            </Stack>
            <Card>
                <Stack spacing={3} sx={{ p: 3 }}>
                    <Stack>
                        <RHFUpload
                            name="coverUrl"
                            maxSize={5000000}
                            onRemove={handleRemoveFile}
                            onDrop={handleSingleDrop}
                            accept={{ 'application/pdf': ['.pdf'], 'application/msword':['.doc'], 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':['docx'] }}
                        />
                    </Stack>

                    <Stack spacing={1.5}>
                        <Typography variant="subtitle2" >
                            Source
                        </Typography>

                        <RHFSelect
                            name="source"
                            size="small"
                            // sx={{
                            //     width: "10rem",
                            //     maxWidth: "10rem"
                            // }}
                        >
                            {SOURCE_OPTIONS.map((item) => (
                                <MenuItem key={item.label} value={item.value}>
                                    {item.label}
                                </MenuItem>
                            ))}
                        </RHFSelect>
                    </Stack>



                    <Stack spacing={1.5}>
                        <Typography variant="subtitle2">Tags</Typography>

                        <Controller
                            name={"tag"}
                            render={({ field, fieldState: { error } }) => (
                                <Autocomplete
                                    {...field}
                                    inputValue={tagInputValue}
                                    onKeyDown={e => { }}
                                    onInputChange={(event, newInputValue) => {
                                        if (newInputValue.endsWith(',')) {
                                            let oldValues = methods.getValues();
                                            oldValues = oldValues.tag;
                                            let newValueArr = newInputValue.split(',').slice(0, -1);
                                            newValueArr = oldValues.concat(newValueArr);
                                            methods.setValue("tag", newValueArr, { shouldValidate: true });
                                            setTagInputValue('');
                                        } else {
                                            setTagInputValue(newInputValue);
                                        }
                                    }}
                                    onChange={(event, newValue) => {
                                        console.log('on change called ', newValue);
                                        return methods.setValue("tag", newValue, { shouldValidate: true });
                                    }}
                                    renderInput={params => (
                                        <TextField
                                            placeholder={"Add tag and press Enter"}
                                            error={!!error}
                                            {...params}
                                            helperText={error ? error?.message : ""}
                                        // onChange={handleSkillChange}
                                        />
                                    )}
                                    multiple
                                    freeSolo
                                    disableCloseOnSelect
                                    options={[]}
                                    renderTags={(selected, getTagProps) =>
                                        selected.map((option, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={option}
                                                label={option}
                                                size="small"
                                                color="info"
                                                variant="soft"
                                            />
                                        ))
                                    }
                                />
                            )}
                        />
                    </Stack>




                    {/* <Stack spacing={1.5}>
                        <Typography variant="subtitle2" >
                            Status
                        </Typography>

                        <RHFSelect
                            name="status"
                            size="small"
                            // sx={{
                            //     width: "10rem",
                            //     maxWidth: "10rem"
                            // }}
                        >
                            {STATUS_OPTIONS.map((item) => (
                                <MenuItem key={item.label} value={item.label}>
                                    {item.label}
                                </MenuItem>
                            ))}
                        </RHFSelect>
                    </Stack> */}


                </Stack>
            </Card>
        </Grid>
    );

    const renderActions = (
        <>
            {/* {mdUp && <Grid md={4} />} */}
            <Grid
                xs={12}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <Stack direction="column" alignItems="center" spacing={1.5}>
                    <Button type="submit" sx={{ width: "10rem" }} size="large" variant="contained">
                        Add Candidate
                    </Button>
                </Stack>
            </Grid>
        </>
    );

    return (
        <>
            {isSubmitting && (
                <Stack
                    sx={{ height: '100%', display: 'flex', justifyContent: 'center' }}
                >
                    <LoadingScreen loadingText="Adding candidate.. Please wait ..." />
                </Stack>
            )}
            {!isSubmitting && (
                <FormProvider methods={methods} onSubmit={onSubmit}>
                    <Grid
                        sx={{ display: 'flex', justifyContent: 'center' }}
                        container
                        spacing={3}
                    >
                        {renderProperties}
                        {renderActions}
                    </Grid>
                </FormProvider>
            )}
        </>
    );
}
